import React, { useState, useContext, useRef, useEffect } from "react";
import axios from "axios";
import { useReports } from "../../context/report-context";
import { NavigationContext } from "../../context/navigation-context";
import { OverlayTrigger, Button, Spinner, Tooltip } from "react-bootstrap";
import { getReportSheet } from "../../util/getReportSheet";
import { API_URL } from "../../config/config";
import ReportButtonForm from "../Modals/ReportButtonForm";

export default function ReportButton({
  reportId,
  body = {},
  placement = "bottom",
  btnColor = "primary",
  isPrimary = false,
  size = "sm",
  style = {},
  showNumber,
}) {
  const { reports } = useReports();
  const { state } = useContext(NavigationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const cancelReportRef = useRef(null);

  const spinnerVariant = btnColor.startsWith("outline-")
    ? btnColor.split("outline-")[1]
    : "light";

  const isConsole = sessionStorage.getItem("clientId") === "console";
  const report = reports.find((r) => r.reportId == reportId);

  useEffect(() => {
    return () => {
      if (cancelReportRef.current) {
        cancelReportRef.current();
      }
    };
  }, []);

  if (!isConsole && (!report)) return null;

  const createCancelHandlers = () => {
    const controller = new AbortController();

    const cancelTokenSource = axios.CancelToken
      ? axios.CancelToken.source()
      : null;

    let canceled = false;

    const cancel = () => {
      if (canceled) return;

      canceled = true;

      if (!controller.signal.aborted) {
        controller.abort("report-canceled");
      }

      if (cancelTokenSource) {
        cancelTokenSource.cancel("report-canceled");
      }
    };

    return {
      signal: controller.signal,
      cancelToken: cancelTokenSource?.token,
      cancel,
    };
  };

  const runReport = (extraData = {}) => {
    const url = `${API_URL}/api/reports/${reportId}`;

    try {
      const requestBody = typeof body === "function" ? body() : body;

      if (!requestBody) {
        setIsLoading(false);
        return;
      }

      const { signal, cancelToken, cancel } = createCancelHandlers();

      cancelReportRef.current = cancel;

      getReportSheet({
        url,
        reportId,
        body: { ...requestBody, ...extraData },
        signal,
        cancelToken,
        callbackToChangeState: setIsLoading,
      }).finally(() => {
        if (cancelReportRef.current === cancel) {
          cancelReportRef.current = null;
        }
      });
    } catch (err) {
      console.error("Błąd w runReport:", err);
      setIsLoading(false);
      cancelReportRef.current = null;
    }
  };

  const handleClick = () => {
    if (reportId === 99) {
      setShowModal(true);
    } else {
      runReport();
    }
  };

  const handleCancelClick = () => {
    if (cancelReportRef.current) {
      cancelReportRef.current();
    }

    setIsLoading(false);
  };

  return (
    <>
      {!isLoading ? (
        <OverlayTrigger
          placement={placement}
          overlay={
            <Tooltip>
              <div>
                <b>
                  Typ raportu:
                  <br />
                  {report?.reportName || (isConsole ? `DZ${reportId}` : "Loading...")}
                  <br />
                  [DZ{reportId || ""}]
                </b>
                <br />
                {isPrimary && !isConsole && report?.reportDescription ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: report.reportDescription || "",
                    }}
                  ></div>
                ) : null}
              </div>
            </Tooltip>
          }
        >
          <Button
            variant=""
            style={style}
            className={`d-inline m-1 btn btn-${btnColor} float-right fas fa-xs ${
              !showNumber && " fa-download"
            }`}
            onClick={handleClick}
          >
            {showNumber && (
              <b
                className="p-0 d-flex justify-content-between"
                style={{ fontFamily: "Arial" }}
              >
                <i className="fas fa-download" />
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ minWidth: "30px" }}
                >
                  {reportId}
                </div>
              </b>
            )}
          </Button>
        </OverlayTrigger>
      ) : (
        <Button
          style={style}
          variant=""
          onClick={handleCancelClick}
          title="Anuluj generowanie raportu"
          className={`d-flex justify-content-center align-items-center m-1 btn btn-${btnColor} float-right`}
        >
          <Spinner
            as="span"
            animation="border"
            size={size}
            role="status"
            aria-hidden="true"
            variant={spinnerVariant}
            className="b-0 pr-0 pl-0"
            style={{ maxHeight: "12px", maxWidth: "12px" }}
          />
        </Button>
      )}

      {showModal && (
        <ReportButtonForm
          show={showModal}
          onHide={() => setShowModal(false)}
          onConfirm={(formData) => {
            setShowModal(false);
            runReport(formData);
          }}
          title={`Raport DZ${reportId}`}
          {...{
            isFromDateTime: true,
            isToDateTime: true,
            isIncludeEmpty: true,
          }}
        />
      )}
    </>
  );
}